// @ts-check
// charat.draw.js — キャラを描く関数（2026-10-20〜）。共通版エンジン（charat.common.js・chibi.js）と新しい作る画面（chibi/create/ui/app.js）の両方が使う。
// 方針: エンジンは1つ。描き方の実装はここだけに置き、メーカーの違いは chibiConfig とカテゴリのデータで切りかえる（tools/chibi-unify/NEW-UI-ENGINE.md）。
// - d3・jQuery を使わない。レイヤーの要素（g#lcart_xxx）や要素の並びを受け取り、その場で描き終わる（アニメーションはエンジンの側で、行き先はここで計算）
// - 中身はエンジンの道具（charat.common.changeColor など）を書き写したもの。エンジンの道具はこれを呼ぶだけにしてある。
//   クラシック版の動きを変えないため、元の書き方の癖（文字のままの比べ方など）もそのまま残し、「注意:」で書いておく
// - 色の読み書きは d3 と同じ: 読むときはインラインの値、無ければ計算後の値（d3 の selection.style）。色の変換は d3.rgb があればそれ、
//   無いページ（新しい作る画面）では同じ結果になる小さな読み取り（#rgb・#rrggbb・rgb()・rgba()・色の名前）
// 型（2026-10-22〜）: JSDoc で付け、tsc で型チェックする（chibi/create/ui/tsconfig.json。// @ts-check）。外から使う関数の形は charat.draw.d.ts の CharatDraw。
//   クラシック版の charat.common.min.js にもつなげて入れるので JS のまま。型のためにコードは変えない（コメントと /** @type */ (...) の注釈だけ）
var charatDraw = (/** @returns {import("./charat.draw").CharatDraw} */ function () {
    "use strict";

    /** @typedef {import("./charat.draw").Category} Category */
    /** @typedef {import("./charat.draw").Rgb} Rgb */
    /** @typedef {import("./charat.draw").ColorLine} ColorLine */
    /** @typedef {import("./charat.draw").PatternLineOptions} PatternLineOptions */

    var NS = "http://www.w3.org/2000/svg";
    // 色を塗り替える図形（changeColor・makePattern）と、線も見る図形（changeRevColor・changePattern・setStrokeWidth）
    var SHAPES = "path,ellipse,polygon,circle,rect";
    var SHAPES_LINE = "path,ellipse,polygon,circle,rect,line,polyline";

    // ---- 読み書き（d3 の selection.style・attr と同じ） ----
    /**
     * @param {Element} node
     * @param {string} name
     * @returns {string}
     */
    function styleGet(node, name) {
        return /** @type {SVGElement} */ (node).style.getPropertyValue(name) || window.getComputedStyle(node, null).getPropertyValue(name);
    }
    /**
     * @param {Element} node
     * @param {string} name
     * @param {string | number | null | undefined} value
     */
    function styleSet(node, name, value) {
        if (value == null) {
            /** @type {SVGElement} */ (node).style.removeProperty(name);
        } else {
            /** @type {SVGElement} */ (node).style.setProperty(name, /** @type {string} */ (value), "");
        }
    }
    /**
     * @template T
     * @param {ArrayLike<T>} list
     * @param {(item: T, index: number) => void} fn
     */
    function each(list, fn) {
        Array.prototype.forEach.call(list, fn);
    }
    // 要素の並びのそれぞれの中で selector に合うものすべて（d3 の selection.selectAll）
    /**
     * @param {ArrayLike<Element>} nodes
     * @param {string} selector
     * @returns {Element[]}
     */
    function allIn(nodes, selector) {
        /** @type {Element[]} */
        var out = [];
        each(nodes, function (n) {
            each(n.querySelectorAll(selector), function (m) {
                out.push(m);
            });
        });
        return out;
    }

    // ---- 色（d3.rgb・d3.interpolateRgb と同じ結果） ----
    // 色の名前（CSS の色名。d3-color と同じ表）
    /** @type {Record<string, number>} */
    var NAMED = {
        aliceblue: 0xf0f8ff, antiquewhite: 0xfaebd7, aqua: 0x00ffff, aquamarine: 0x7fffd4, azure: 0xf0ffff, beige: 0xf5f5dc,
        bisque: 0xffe4c4, black: 0x000000, blanchedalmond: 0xffebcd, blue: 0x0000ff, blueviolet: 0x8a2be2, brown: 0xa52a2a,
        burlywood: 0xdeb887, cadetblue: 0x5f9ea0, chartreuse: 0x7fff00, chocolate: 0xd2691e, coral: 0xff7f50,
        cornflowerblue: 0x6495ed, cornsilk: 0xfff8dc, crimson: 0xdc143c, cyan: 0x00ffff, darkblue: 0x00008b, darkcyan: 0x008b8b,
        darkgoldenrod: 0xb8860b, darkgray: 0xa9a9a9, darkgreen: 0x006400, darkgrey: 0xa9a9a9, darkkhaki: 0xbdb76b,
        darkmagenta: 0x8b008b, darkolivegreen: 0x556b2f, darkorange: 0xff8c00, darkorchid: 0x9932cc, darkred: 0x8b0000,
        darksalmon: 0xe9967a, darkseagreen: 0x8fbc8f, darkslateblue: 0x483d8b, darkslategray: 0x2f4f4f, darkslategrey: 0x2f4f4f,
        darkturquoise: 0x00ced1, darkviolet: 0x9400d3, deeppink: 0xff1493, deepskyblue: 0x00bfff, dimgray: 0x696969,
        dimgrey: 0x696969, dodgerblue: 0x1e90ff, firebrick: 0xb22222, floralwhite: 0xfffaf0, forestgreen: 0x228b22,
        fuchsia: 0xff00ff, gainsboro: 0xdcdcdc, ghostwhite: 0xf8f8ff, gold: 0xffd700, goldenrod: 0xdaa520, gray: 0x808080,
        green: 0x008000, greenyellow: 0xadff2f, grey: 0x808080, honeydew: 0xf0fff0, hotpink: 0xff69b4, indianred: 0xcd5c5c,
        indigo: 0x4b0082, ivory: 0xfffff0, khaki: 0xf0e68c, lavender: 0xe6e6fa, lavenderblush: 0xfff0f5, lawngreen: 0x7cfc00,
        lemonchiffon: 0xfffacd, lightblue: 0xadd8e6, lightcoral: 0xf08080, lightcyan: 0xe0ffff, lightgoldenrodyellow: 0xfafad2,
        lightgray: 0xd3d3d3, lightgreen: 0x90ee90, lightgrey: 0xd3d3d3, lightpink: 0xffb6c1, lightsalmon: 0xffa07a,
        lightseagreen: 0x20b2aa, lightskyblue: 0x87cefa, lightslategray: 0x778899, lightslategrey: 0x778899,
        lightsteelblue: 0xb0c4de, lightyellow: 0xffffe0, lime: 0x00ff00, limegreen: 0x32cd32, linen: 0xfaf0e6, magenta: 0xff00ff,
        maroon: 0x800000, mediumaquamarine: 0x66cdaa, mediumblue: 0x0000cd, mediumorchid: 0xba55d3, mediumpurple: 0x9370db,
        mediumseagreen: 0x3cb371, mediumslateblue: 0x7b68ee, mediumspringgreen: 0x00fa9a, mediumturquoise: 0x48d1cc,
        mediumvioletred: 0xc71585, midnightblue: 0x191970, mintcream: 0xf5fffa, mistyrose: 0xffe4e1, moccasin: 0xffe4b5,
        navajowhite: 0xffdead, navy: 0x000080, oldlace: 0xfdf5e6, olive: 0x808000, olivedrab: 0x6b8e23, orange: 0xffa500,
        orangered: 0xff4500, orchid: 0xda70d6, palegoldenrod: 0xeee8aa, palegreen: 0x98fb98, paleturquoise: 0xafeeee,
        palevioletred: 0xdb7093, papayawhip: 0xffefd5, peachpuff: 0xffdab9, peru: 0xcd853f, pink: 0xffc0cb, plum: 0xdda0dd,
        powderblue: 0xb0e0e6, purple: 0x800080, rebeccapurple: 0x663399, red: 0xff0000, rosybrown: 0xbc8f8f, royalblue: 0x4169e1,
        saddlebrown: 0x8b4513, salmon: 0xfa8072, sandybrown: 0xf4a460, seagreen: 0x2e8b57, seashell: 0xfff5ee, sienna: 0xa0522d,
        silver: 0xc0c0c0, skyblue: 0x87ceeb, slateblue: 0x6a5acd, slategray: 0x708090, slategrey: 0x708090, snow: 0xfffafa,
        springgreen: 0x00ff7f, steelblue: 0x4682b4, tan: 0xd2b48c, teal: 0x008080, thistle: 0xd8bfd8, tomato: 0xff6347,
        turquoise: 0x40e0d0, violet: 0xee82ee, wheat: 0xf5deb3, white: 0xffffff, whitesmoke: 0xf5f5f5, yellow: 0xffff00,
        yellowgreen: 0x9acd32
    };
    // 色の文字 → { r, g, b, opacity }（読めなければ全部 NaN。d3.rgb と同じ）
    /**
     * @param {unknown} s
     * @returns {Rgb}
     */
    function parseColor(s) {
        var str = String(s == null ? "" : s).trim().toLowerCase();
        var m;
        if ((m = /^#([0-9a-f]{3,8})$/.exec(str))) {
            var h = m[1];
            var n = parseInt(h, 16);
            if (h.length === 6) return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255, opacity: 1 };
            if (h.length === 3) return { r: ((n >> 8) & 15) * 17, g: ((n >> 4) & 15) * 17, b: (n & 15) * 17, opacity: 1 };
            if (h.length === 8) return { r: (n >>> 24) & 255, g: (n >> 16) & 255, b: (n >> 8) & 255, opacity: (n & 255) / 255 };
            if (h.length === 4) return { r: ((n >> 12) & 15) * 17, g: ((n >> 8) & 15) * 17, b: ((n >> 4) & 15) * 17, opacity: ((n & 15) * 17) / 255 };
        }
        var num = "\\s*([+-]?\\d*\\.?\\d+(?:[eE][+-]?\\d+)?)\\s*";
        if ((m = new RegExp("^rgb\\(" + num + "," + num + "," + num + "\\)$").exec(str))) {
            return { r: +m[1], g: +m[2], b: +m[3], opacity: 1 };
        }
        if ((m = new RegExp("^rgba\\(" + num + "," + num + "," + num + "," + num + "\\)$").exec(str))) {
            return { r: +m[1], g: +m[2], b: +m[3], opacity: +m[4] };
        }
        if (Object.prototype.hasOwnProperty.call(NAMED, str)) {
            var v = NAMED[str];
            return { r: (v >> 16) & 255, g: (v >> 8) & 255, b: v & 255, opacity: 1 };
        }
        if (str === "transparent") return { r: NaN, g: NaN, b: NaN, opacity: 0 };
        return { r: NaN, g: NaN, b: NaN, opacity: NaN };
    }
    /** @param {number} v */
    function clampByte(v) {
        return Math.max(0, Math.min(255, Math.round(v) || 0));
    }
    // d3.rgb(色).toString() と同じ書き方（rgb(r, g, b)。透明度が 1 未満なら rgba）
    /**
     * @param {Rgb} c
     * @returns {string}
     */
    function rgbString(c) {
        var a = isNaN(c.opacity) ? 1 : Math.max(0, Math.min(1, c.opacity));
        return (a === 1 ? "rgb(" : "rgba(") + clampByte(c.r) + ", " + clampByte(c.g) + ", " + clampByte(c.b) + (a === 1 ? ")" : ", " + a + ")");
    }
    /**
     * @param {unknown} s
     * @returns {Rgb}
     */
    function rgbOf(s) {
        return window.d3 && window.d3.rgb ? window.d3.rgb(s) : parseColor(s);
    }
    // #rrggbb → rgb(r, g, b)（charat.common.convToRgb）
    /**
     * @param {string | undefined} s
     * @returns {string}
     */
    function toRgb(s) {
        return window.d3 && window.d3.rgb ? window.d3.rgb(s).toString() : rgbString(parseColor(s));
    }
    // rgb(r, g, b) → #rrggbb（charat.common.convToHexRgb。読めない色は "#aNaNaN" になる。元と同じ）
    /**
     * @param {string} s
     * @returns {string}
     */
    function toHex(s) {
        var c = rgbOf(s);
        return "#" + ("0" + c.r.toString(16)).substr(-2) + ("0" + c.g.toString(16)).substr(-2) + ("0" + c.b.toString(16)).substr(-2);
    }
    // #rrggbb の各色を足し引きする（charat.common.addRgb・addColor）
    /**
     * @param {string} col
     * @param {number} addcol
     */
    function addColor(col, addcol) {
        return ("0" + Math.max(0, Math.min(255, parseInt(col, 16) + addcol)).toString(16)).substr(-2);
    }
    /**
     * @param {string} rgbstr
     * @param {number} r
     * @param {number} g
     * @param {number} b
     * @returns {string}
     */
    function addRgb(rgbstr, r, g, b) {
        return "#" + addColor(rgbstr.substr(1, 2), r) + addColor(rgbstr.substr(3, 2), g) + addColor(rgbstr.substr(5, 2), b);
    }
    // 色の反転（charat.common.inverseColor）
    /**
     * @param {string} baseColor
     * @returns {string}
     */
    function inverseColor(baseColor) {
        baseColor = baseColor.replace("#", "");
        if (baseColor.length != 6) {
            return "#000000";
        }
        var newColor = "";
        for (var x = 0; x < 3; x++) {
            /** @type {any} 数のあとに文字（16進）を入れる。元の書き方のまま */
            var colorWK = 255 - parseInt(baseColor.substr(x * 2, 2), 16);
            if (colorWK < 0) {
                colorWK = 0;
            } else {
                colorWK = colorWK.toString(16);
            }
            if (colorWK.length < 2) {
                colorWK = "0" + colorWK;
            }
            newColor += colorWK;
        }
        return "#" + newColor;
    }
    // from → to の間の色（d3.interpolateRgb(from, to)(t)）
    /**
     * @param {string} from
     * @param {string} to
     * @returns {(t: number) => string}
     */
    function interpolateRgb(from, to) {
        if (window.d3 && window.d3.interpolateRgb) return window.d3.interpolateRgb(from, to);
        var a = parseColor(from), b = parseColor(to);
        return function (t) {
            return rgbString({ r: a.r + (b.r - a.r) * t, g: a.g + (b.g - a.g) * t, b: a.b + (b.b - a.b) * t, opacity: 1 });
        };
    }

    // ---- パーツを入れる（charat.common.changeParts の中身） ----
    // パーツの g の中のグラデーションの id をキャラ側だけ付け替える（カテゴリの一覧の絵と同じ id にならないように。charat.common.replaceGrad）
    /**
     * @param {string} aHtml
     * @returns {string}
     */
    function replaceGrad(aHtml) {
        return aHtml.replace(/SVGID_/g, "SVGCID_").replace(/XMLID_/g, "XMLCID_");
    }
    // レイヤーにパーツの層（x / a / y の g）の写しを入れて、入れた要素を返す（transform は付けない）
    /**
     * @param {Element} layer
     * @param {Element} src
     * @returns {Element}
     */
    function putPart(layer, src) {
        var holder = document.createElementNS(NS, "svg");
        holder.innerHTML = replaceGrad(src.outerHTML);
        var node = /** @type {Element} */ (holder.firstElementChild);
        layer.appendChild(node);
        return node;
    }
    // パーツの層: カテゴリの TGLAYER の順に、各レイヤーに入れる層（TGLAYER が空ならどれも x。無ければ null）
    /**
     * @template T
     * @param {Category} category
     * @param {{ x?: T, a?: T, y?: T }} part
     * @returns {(T | null)[]}
     */
    function partLayers(category, part) {
        return category.LAYER.map(function (layerName, i) {
            if (category.TGLAYER.length == 0) {
                return part.x != undefined ? part.x : null;
            }
            var head = category.TGLAYER[i];
            return (head == "x" || head == "a" || head == "y") && part[head] != undefined ? /** @type {T} */ (part[head]) : null;
        });
    }

    // ---- 色（charat.common.changeColor の1レイヤー分） ----
    // 左右の色（sel 1・2）の対象: レイヤーの中の2つの g（layer > g > g > g が2つならそれ、無ければ layer > g > g）
    /**
     * @param {Element} layer
     * @returns {NodeListOf<Element> | null}
     */
    function lrTarget(layer) {
        var three = layer.querySelectorAll(":scope > g > g > g");
        if (three.length == 2) return three;
        var two = layer.querySelectorAll(":scope > g > g");
        if (two.length == 2) return two;
        return null;
    }
    // 並びのうち、親の中で k 番目の子のもの（d3 の filter(":nth-child(k)")）
    /**
     * @param {ArrayLike<Element>} nodes
     * @param {number} k
     * @returns {Element[]}
     */
    function nthChild(nodes, k) {
        return Array.prototype.filter.call(nodes, function (/** @type {Element} */ n) {
            return n.matches(":nth-child(" + k + ")");
        });
    }
    // パーツの g の中の最初の path の d の最初の x（charat.common.getXFromGPath）。注意: 文字のまま返す（左右の判定は文字のまま比べている。元と同じ）
    /**
     * @param {ArrayLike<Element>} nodes
     * @returns {any} 文字のまま返す（下の注意）
     */
    function xFromG(nodes) {
        /** @type {Element | null} */
        var path = null;
        for (var i = 0; i < nodes.length && !path; i++) {
            path = nodes[i].querySelector("path");
        }
        if (!path) {
            return 0;
        }
        var dstr = path.getAttribute("d");
        if (!dstr) {
            return 0;
        }
        var sppos = dstr.indexOf(",");
        /** @type {any} 数のあとに文字を入れる。元の書き方のまま */
        var posX = 0;
        if (sppos >= 1) {
            posX = dstr.substring(1, sppos);
        }
        return posX;
    }
    // 1レイヤーの色を変える。sel: 0 全体・1 左・2 右（左右は画面に向かって見た位置）。aOrgColor: [基本色, 影色] を直接指定するとき
    // 元の塗りは属性 orgb（基本色の所）・drgb（影色の所）に退避し、2回目からはそこを見て塗り直す
    /**
     * @param {Element | null} layer
     * @param {Category} aCategory
     * @param {string} aColor
     * @param {number} [aSel]
     * @param {[string, string]} [aOrgColor]
     */
    function colorLayer(layer, aCategory, aColor, aSel, aOrgColor) {
        if (!layer || aCategory.COLOR[0] == undefined) {
            return;
        }
        var baseRgb = toRgb(aCategory.COLOR[0]);
        var darkRgb = aCategory.COLOR.length > 1 ? toRgb(aCategory.COLOR[1]) : "";
        var bColor = aOrgColor == undefined ? aColor : aOrgColor[0];
        var dColor =
            aOrgColor == undefined
                ? aColor.toLowerCase() == aCategory.COLOR[0].toLowerCase() && aCategory.COLOR.length > 1
                    ? darkRgb
                    : addRgb(aColor, -25, -50, -40)
                : aOrgColor[1];
        var targets;
        if (aSel == 1 || aSel == 2) {
            var lr = lrTarget(layer);
            if (lr == null) {
                return;
            }
            var x1 = xFromG(nthChild(lr, aSel == 1 ? 1 : 2));
            var x2 = xFromG(nthChild(lr, aSel == 1 ? 2 : 1));
            var lrindex = aSel == 1 ? 1 : 2;
            if (lrindex == 1 && x1 > x2) {
                lrindex = 2;
            } else if (lrindex == 2 && x1 < x2) {
                lrindex = 1;
            }
            targets = allIn(nthChild(lr, lrindex), SHAPES);
        } else {
            targets = layer.querySelectorAll(SHAPES);
        }
        // 変更済みの色を変更
        var isChanged = false;
        each(targets, function (n) {
            if (n.getAttribute("orgb") == baseRgb) {
                styleSet(n, "fill", bColor);
                isChanged = true;
            } else if (n.getAttribute("drgb") == darkRgb) {
                styleSet(n, "fill", dColor);
                isChanged = true;
            }
        });
        if (!isChanged) {
            // 新規で色を変更
            each(targets, function (n) {
                var fill = styleGet(n, "fill");
                if (fill == baseRgb) {
                    n.setAttribute("orgb", fill);
                    styleSet(n, "fill", bColor);
                } else if (fill == darkRgb) {
                    n.setAttribute("drgb", fill);
                    styleSet(n, "fill", dColor);
                }
            });
        }
    }

    // ---- 色の反転（charat.common.changeRevColor の1レイヤー分） ----
    // aPtnRev: 0 元の色に戻す・1 2色（aColor1 ⇔ aColor2、線は aStroke1 ⇔ aStroke2）の入れかえ・2 RGB 反転
    /**
     * @param {Element | null} layer
     * @param {number} aPtnRev
     * @param {string} aColor1
     * @param {string} aColor2
     * @param {string} aStroke1
     * @param {string} aStroke2
     */
    function revColorLayer(layer, aPtnRev, aColor1, aColor2, aStroke1, aStroke2) {
        if (!layer) {
            return;
        }
        each(layer.querySelectorAll(SHAPES_LINE), function (n) {
            var frgb = styleGet(n, "fill");
            var strrgb = styleGet(n, "stroke");
            var orgcol, orgstroke;
            if (aPtnRev == 0) {
                // 元の色に戻す
                if (frgb != undefined) {
                    orgcol = n.getAttribute("orgb");
                    if (orgcol != undefined && orgcol !== "") {
                        styleSet(n, "fill", orgcol);
                    }
                }
                if (strrgb != undefined) {
                    orgstroke = n.getAttribute("ostroke");
                    if (orgstroke != undefined && orgstroke !== "") {
                        styleSet(n, "stroke", orgstroke);
                    }
                }
            } else if (aPtnRev == 2) {
                // RGB 反転
                if (frgb != undefined) {
                    orgcol = n.getAttribute("orgb");
                    if (orgcol == undefined || orgcol === "") {
                        n.setAttribute("orgb", frgb);
                        orgcol = frgb;
                    }
                    styleSet(n, "fill", inverseColor(toHex(orgcol)));
                }
                if (strrgb != undefined) {
                    orgstroke = n.getAttribute("ostroke");
                    if (orgstroke == undefined || orgstroke === "") {
                        n.setAttribute("ostroke", strrgb);
                        orgstroke = strrgb;
                    }
                    styleSet(n, "stroke", inverseColor(toHex(orgstroke)));
                }
            } else if (aPtnRev == 1) {
                // 指定色の入れかえ（元の色を基準に）
                if (frgb != undefined) {
                    orgcol = n.getAttribute("orgb");
                    if (orgcol == undefined || orgcol === "") {
                        n.setAttribute("orgb", frgb);
                        orgcol = frgb;
                    }
                    var hexOrgCol = toHex(orgcol);
                    styleSet(n, "fill", hexOrgCol === aColor1 ? aColor2 : hexOrgCol === aColor2 ? aColor1 : hexOrgCol);
                }
                if (strrgb != undefined) {
                    orgstroke = n.getAttribute("ostroke");
                    if (orgstroke == undefined || orgstroke === "") {
                        n.setAttribute("ostroke", strrgb);
                        orgstroke = strrgb;
                    }
                    var hexOrgStroke = toHex(orgstroke);
                    styleSet(n, "stroke", hexOrgStroke === aStroke1 ? aStroke2 : hexOrgStroke === aStroke2 ? aStroke1 : hexOrgStroke);
                }
            }
        });
    }

    // ---- 柄を貼る（charat.common.changePattern の1レイヤー分） ----
    // カテゴリの基本色（COLOR[0]）の所に aPatternId の柄、影色（COLOR[1]）の所に aPatternDkId の柄（url(#id)）。
    // 元の塗りは orgb・drgb に退避し、柄の id が null ならそこから元の塗りに戻す。
    // aPtnMode: 柄を付けたときの色反転（paramPtnMode）。0 以外なら反転したあとの色（aColor1 ⇔ aColor2）を探す
    /**
     * @param {Category} aCategory
     * @param {number} aPtnMode
     * @param {string} aColor1
     * @param {string} aColor2
     * @returns {string[]}
     */
    function patternTargets(aCategory, aPtnMode, aColor1, aColor2) {
        var baseRgb = toRgb(aCategory.COLOR[0]);
        var revRgb = toRgb(aCategory.COLOR[0].toLowerCase() == aColor1.toLowerCase() ? aColor2 : aCategory.COLOR[0]);
        if (aPtnMode != 0) {
            baseRgb = revRgb;
        }
        // 注意: COLOR が1色だけのカテゴリでは影色が toRgb(undefined) の "rgb(0, 0, 0)" になる（元と同じ）
        var shadeRgb = aCategory.COLOR.length > 1 ? toRgb(aCategory.COLOR[1]) : "";
        var revShadeRgb = toRgb(
            aCategory && aCategory.COLOR && aCategory.COLOR[1] && aCategory.COLOR[1].toLowerCase() == aColor1.toLowerCase()
                ? aColor2
                : aCategory.COLOR[1]
        );
        if (aPtnMode != 0) {
            shadeRgb = revShadeRgb;
        }
        return [baseRgb, shadeRgb];
    }
    /**
     * @param {Element | null} layer
     * @param {Category} aCategory
     * @param {string | null} aPatternId
     * @param {string | null} aPatternDkId
     * @param {number} aPtnMode
     * @param {string} aColor1
     * @param {string} aColor2
     */
    function patternLayer(layer, aCategory, aPatternId, aPatternDkId, aPtnMode, aColor1, aColor2) {
        if (!layer) {
            return;
        }
        var patternIds = [aPatternId, aPatternDkId];
        var targetRgbs = patternTargets(aCategory, aPtnMode, aColor1, aColor2);
        var backupAttrs = ["orgb", "drgb"];
        var shapes = layer.querySelectorAll(SHAPES_LINE);
        for (var k = 0; k < 2; k++) {
            if (patternIds[k] != null) {
                // 柄を付ける（元の塗りは退避）。退避した色があれば先に見る
                each(shapes, function (n) {
                    var orgb = n.getAttribute(backupAttrs[k]);
                    if (orgb == targetRgbs[k]) {
                        styleSet(n, "fill", "url(#" + patternIds[k] + ")");
                    } else if (orgb != undefined && orgb !== "") {
                        // ほかの色を退避済みなので何もしない
                    } else {
                        var fill = styleGet(n, "fill");
                        if (fill == targetRgbs[k]) {
                            n.setAttribute(backupAttrs[k], fill);
                            styleSet(n, "fill", "url(#" + patternIds[k] + ")");
                        }
                    }
                });
            } else {
                // 元に戻す（線の色 ostroke は基本色の回でだけ戻す）
                each(shapes, function (n) {
                    var back = n.getAttribute(backupAttrs[k]);
                    if (back != undefined && back !== "") {
                        styleSet(n, "fill", back);
                        n.setAttribute(backupAttrs[k], "");
                    }
                    if (k == 0) {
                        var ostroke = n.getAttribute("ostroke");
                        if (ostroke != undefined && ostroke !== "") {
                            styleSet(n, "stroke", ostroke);
                            n.setAttribute("ostroke", "");
                        }
                    }
                });
            }
        }
    }

    // ---- 柄の <pattern> を作る（charat.common.copyPattern・makePattern） ----
    // defs の <pattern id=aPtnDefId> に柄の g（sPattern）を写して色を付ける（既にあれば中身を差し替える）。
    //   aPtnRev: undefined 反転なし・1 2色（aRevCol1 ⇔ aRevCol2、線は aRevStrokeCol1 ⇔ aRevStrokeCol2）の入れかえ・2 RGB 反転
    //   aColor: 柄の地の色（aRevCol1 の塗りと aRevStrokeCol1 の線をこの色に）。isDark: 影色用（白い所も影の白に）
    //   size・adjust: <pattern> の大きさと、柄の座標のずらし（chibi.SIZE_PATTERN 50・ADJUST_PATTERN -94.5）
    /**
     * @param {Element} aDef
     * @param {Element} sPattern
     * @param {string} aPtnDefId
     * @param {number | undefined} aPtnRev
     * @param {string} aColor
     * @param {string} aRevCol1
     * @param {string} aRevCol2
     * @param {string} aRevStrokeCol1
     * @param {string} aRevStrokeCol2
     * @param {boolean} isDark
     * @param {number} size
     * @param {number} adjust
     */
    function makePattern(aDef, sPattern, aPtnDefId, aPtnRev, aColor, aRevCol1, aRevCol2, aRevStrokeCol1, aRevStrokeCol2, isDark, size, adjust) {
        var clipptn = aDef.querySelector("#" + aPtnDefId + " > g");
        if (!clipptn) {
            var pattern = document.createElementNS(NS, "pattern");
            pattern.setAttribute("id", aPtnDefId);
            pattern.setAttribute("patternUnits", "userSpaceOnUse");
            pattern.setAttribute("x", 0);
            pattern.setAttribute("y", 0);
            pattern.setAttribute("width", size);
            pattern.setAttribute("height", size);
            clipptn = document.createElementNS(NS, "g");
            clipptn.setAttribute("transform", "translate(" + adjust + "," + adjust + "), scale(1,1)");
            pattern.appendChild(clipptn);
            aDef.appendChild(pattern);
        } else {
            var old = clipptn.querySelector("g");
            if (old) {
                old.remove();
            }
        }
        clipptn.appendChild(sPattern.cloneNode(true));
        var shapes = clipptn.querySelectorAll(SHAPES);
        if (aPtnRev != undefined) {
            if (aPtnRev == 2) {
                // RGB 反転
                each(shapes, function (n) {
                    var orgcol = styleGet(n, "fill");
                    if (orgcol != undefined) {
                        styleSet(n, "fill", inverseColor(toHex(orgcol)));
                    }
                    var orgstroke = styleGet(n, "stroke");
                    if (orgstroke != undefined) {
                        styleSet(n, "stroke", inverseColor(toHex(orgstroke)));
                    }
                });
            } else if (aPtnRev == 1) {
                // 2色の入れかえ
                each(shapes, function (n) {
                    var orgcol = styleGet(n, "fill");
                    if (orgcol != undefined) {
                        var hexOrgCol = toHex(orgcol);
                        if (hexOrgCol === aRevCol1) {
                            styleSet(n, "fill", aRevCol2);
                        } else if (hexOrgCol === aRevCol2) {
                            styleSet(n, "fill", aRevCol1);
                        }
                    }
                    var orgstroke = styleGet(n, "stroke");
                    if (orgstroke != undefined) {
                        var hexOrgStroke = toHex(orgstroke);
                        if (hexOrgStroke === aRevStrokeCol1) {
                            styleSet(n, "stroke", aRevStrokeCol2);
                        } else if (hexOrgStroke === aRevStrokeCol2) {
                            styleSet(n, "stroke", aRevStrokeCol1);
                        }
                    }
                });
            }
        }
        // 地の色を付ける（aRevCol1 の所を aColor に。影色用は白も影の白に）
        if (aColor != undefined && aColor !== "") {
            var darkWhite = addRgb("#FFFFFF", -25, -50, -40);
            var hexWhite = toHex("#FFFFFF");
            each(shapes, function (n) {
                var orgcol = styleGet(n, "fill");
                if (orgcol != undefined) {
                    var hexOrgCol = toHex(orgcol);
                    if (hexOrgCol === aRevCol1) {
                        styleSet(n, "fill", aColor);
                    }
                    if (isDark == true && hexOrgCol === hexWhite) {
                        styleSet(n, "fill", darkWhite);
                    }
                }
                var orgstroke = styleGet(n, "stroke");
                if (orgstroke != undefined && toHex(orgstroke) === aRevStrokeCol1) {
                    styleSet(n, "stroke", aColor);
                }
            });
        }
    }
    // 柄の一覧（柄の g の配列。id は柄の番号）から番号 aPtnNo の柄を探し、基本色用（aPtnDefId）と影色用（aPtnDkDefId）の2つの <pattern> を作る。
    // 見つかれば true。aPtnMode が 0 以外（色反転中に柄を付けた）なら反転の有無を逆にする（注意: 2（RGB 反転）のときも 0 になる。元と同じ）。
    // aShadeColor: 影色用の地の色（省略すると aColor を暗くした色。エンジンはいつもこちら）
    // 柄に掛ける色の入れかえ: 柄を付けたときに入れかえていた（aPtnMode が 0 でない）なら逆に（注意: 2 も 0 になる。元と同じ）
    /**
     * @param {number} aPtnRev
     * @param {number} aPtnMode
     * @returns {number}
     */
    function patternRev(aPtnRev, aPtnMode) {
        if (aPtnMode != 0) {
            return aPtnRev == 0 ? 1 : 0;
        }
        return aPtnRev;
    }
    /**
     * @param {Element[]} aPatternList
     * @param {Element} aDef
     * @param {string} aPtnDefId
     * @param {string} aPtnDkDefId
     * @param {string} aPtnNo
     * @param {number} aPtnRev
     * @param {number} aPtnMode
     * @param {string} aRevCol1
     * @param {string} aRevCol2
     * @param {string} aRevStrokeCol1
     * @param {string} aRevStrokeCol2
     * @param {string} aColor
     * @param {number} size
     * @param {number} adjust
     * @param {string} [aShadeColor]
     * @returns {boolean}
     */
    function copyPattern(aPatternList, aDef, aPtnDefId, aPtnDkDefId, aPtnNo, aPtnRev, aPtnMode, aRevCol1, aRevCol2, aRevStrokeCol1, aRevStrokeCol2, aColor, size, adjust, aShadeColor) {
        for (var i = 0; i < aPatternList.length; i++) {
            if (aPatternList[i].id == aPtnNo) {
                aPtnRev = patternRev(aPtnRev, aPtnMode);
                var shadeColor = aShadeColor != undefined ? aShadeColor : addRgb(aColor, -25, -50, -40);
                var rev = aPtnRev != 0 ? aPtnRev : undefined;
                makePattern(aDef, aPatternList[i], aPtnDefId, rev, aColor, aRevCol1, aRevCol2, aRevStrokeCol1, aRevStrokeCol2, false, size, adjust);
                makePattern(aDef, aPatternList[i], aPtnDkDefId, rev, shadeColor, aRevCol1, aRevCol2, aRevStrokeCol1, aRevStrokeCol2, true, size, adjust);
                return true;
            }
        }
        return false;
    }

    // ---- 保存データの1行ぶんの色と柄（chibi.makeCharactor・chibi.setPartsColorPattern と同じ決まり。新しい作る画面も同じものを呼ぶ） ----
    // line: { col, lfrom, rfrom, from, to }（保存データの全体の色・左の色・右の色・グラデーションの上・下）
    // 全体の色があれば塗り、続けて左右の色（保存データにあれば。カテゴリの IS_SEL は見ない）。グラデーションは上と下の両方があれば
    /**
     * @param {ArrayLike<Element | null>} layers
     * @param {Category} aCategory
     * @param {ColorLine} line
     */
    function colorLine(layers, aCategory, line) {
        if (line.col != null && line.col !== "") {
            each(layers, function (g) {
                colorLayer(g, aCategory, line.col);
            });
            if (line.lfrom) {
                each(layers, function (g) {
                    colorLayer(g, aCategory, line.lfrom, 1);
                });
            }
            if (line.rfrom) {
                each(layers, function (g) {
                    colorLayer(g, aCategory, line.rfrom, 2);
                });
            }
        }
        if (line.from && line.to) {
            each(layers, function (g) {
                gradLayer(g, aCategory, line.from, line.to);
            });
        }
    }
    // 1行ぶんの柄と色の反転。ptn: 柄の番号（"" なら柄なし）、prev: 色の入れかえ（0 なし・1 2色・2 RGB）、mode: 柄を付けたときの入れかえ、col: 全体の色。
    // o: { defs: 柄を置く defs, list: 柄の一覧（g の id が柄の番号。無ければ柄は貼らない）, size, adjust,
    //      ex1・ex2・stroke1・stroke2: 入れかえる2色と線の2色（chibi.COLOR_EX1 など）,
    //      partsColor: true なら柄の地の色を「全体の色、無ければパーツの元の色」に、影の地を色と同じ決まりに（設定 PATTERN_PARTS_COLOR。既定 false は全体の色だけ＝無ければ柄の灰色のまま）,
    //      revParts: false なら色の入れかえでパーツそのものの色は入れかえない（設定 PATTERN_REV_PARTS。既定 true） }
    // 柄を貼ったら true（エンジンはこのあとレイヤーに paramPtn などを入れる）
    /**
     * @param {ArrayLike<Element | null>} layers
     * @param {Category} aCategory
     * @param {string} ptn
     * @param {number} prev
     * @param {number} mode
     * @param {string} col
     * @param {PatternLineOptions} o
     * @returns {boolean}
     */
    function patternLine(layers, aCategory, ptn, prev, mode, col, o) {
        var revParts = o.revParts !== false;
        if (prev != 0 && ptn == "") {
            // 柄なしで色の入れかえだけ
            if (revParts) {
                each(layers, function (g) {
                    revColorLayer(g, prev, o.ex1, o.ex2, o.stroke1, o.stroke2);
                });
            }
            return false;
        }
        if (ptn == "" || !o.list) {
            return false;
        }
        var ground = col;
        var shade;
        if (o.partsColor) {
            ground = col || aCategory.COLOR[0].toLowerCase();
            shade = ground.toLowerCase() === aCategory.COLOR[0].toLowerCase() && aCategory.COLOR.length > 1 ? aCategory.COLOR[1] : addRgb(ground, -25, -50, -40);
        }
        var idBase = "cpattern_" + aCategory.LAYER[0];
        var idDark = "cpatterndk_" + aCategory.LAYER[0];
        // defs が無いときは作らずに、柄が一覧にあるかだけ見て先へ進む（エンジンの copyPattern と同じ）
        var found = false;
        if (o.defs) {
            found = copyPattern(o.list, o.defs, idBase, idDark, ptn, prev, mode, o.ex1, o.ex2, o.stroke1, o.stroke2, ground, o.size, o.adjust, shade);
        } else {
            for (var i = 0; i < o.list.length && !found; i++) {
                found = o.list[i].id == ptn;
            }
        }
        if (!found) {
            return false;
        }
        each(layers, function (g) {
            if (revParts) {
                revColorLayer(g, prev, o.ex1, o.ex2, o.stroke1, o.stroke2);
            }
            patternLayer(g, aCategory, idBase, idDark, mode, o.ex1, o.ex2);
        });
        return true;
    }

    // ---- グラデーション（charat.common.changeGradColor の1レイヤー分） ----
    // パーツの linearGradient の stop（offset 0・0.5・1）を、上（aFromColor）〜下（aToColor）の色に。
    // offset 0 は GRAD[0]・1 は GRAD[1] の所だけ、0.5 はどれも。元の色は stop の orgb に退避する
    /**
     * @param {Element | null} layer
     * @param {Category} aCategory
     * @param {string} aFromColor
     * @param {string} aToColor
     */
    function gradLayer(layer, aCategory, aFromColor, aToColor) {
        if (!layer) {
            return;
        }
        var colors = interpolateRgb(aToColor, aFromColor);
        var offset = [0, 0.5, 1];
        var baseRgb = [toRgb(aCategory.GRAD[0]), "", toRgb(aCategory.GRAD[1])];
        var stops = offset.map(function (o) {
            return layer.querySelectorAll("linearGradient > stop[offset='" + o + "']");
        });
        var isChanged = false;
        // 変更済みの色を変更
        offset.forEach(function (o, k) {
            each(stops[k], function (n) {
                if ((baseRgb[k] == "" && isChanged) || n.getAttribute("orgb") == baseRgb[k]) {
                    styleSet(n, "stop-color", colors(o));
                    isChanged = true;
                }
            });
        });
        if (!isChanged) {
            // 新規で色を変更
            offset.forEach(function (o, k) {
                each(stops[k], function (n) {
                    var col = styleGet(n, "stop-color");
                    if (baseRgb[k] == "" || col == baseRgb[k]) {
                        n.setAttribute("orgb", col);
                        styleSet(n, "stop-color", colors(o));
                    }
                });
            });
        }
    }

    // ---- 線の太さ（charat.common.setStrokeWidth の1レイヤー分） ----
    /**
     * @param {Element | null} layer
     * @param {string | number} aWidth
     */
    function strokeWidthLayer(layer, aWidth) {
        if (!layer) {
            return;
        }
        each(layer.querySelectorAll(SHAPES_LINE), function (n) {
            if (styleGet(n, "stroke-width") != undefined) {
                styleSet(n, "stroke-width", aWidth);
            }
        });
    }

    // ---- うすさ（charat.common.OpacityG の1レイヤー分） ----
    // くち（mouth）は .lip の所だけ、アイシャドウ（eyeshadow）は root の中の .eyeshadow1〜4 すべて、それ以外はレイヤーの g に opacity
    /**
     * @param {Element} root
     * @param {Element | null} layer
     * @param {string} layerName
     * @param {string | number} opacity
     */
    function opacityLayer(root, layer, layerName, opacity) {
        if (!layer) {
            return;
        }
        if (layerName === "eyeshadow") {
            ["eyeshadow1", "eyeshadow2", "eyeshadow3", "eyeshadow4"].forEach(function (cls) {
                each(root.querySelectorAll("." + cls), function (n) {
                    n.setAttribute("opacity", opacity);
                });
            });
        } else if (layerName === "mouth") {
            each(layer.querySelectorAll(".lip"), function (n) {
                n.setAttribute("opacity", opacity);
            });
        } else {
            layer.setAttribute("opacity", opacity);
        }
    }

    // ---- 位置・拡大・反転・回転の transform ----
    // パーツの拡大縮小（レイヤーの直下の g に付ける。orgW・orgH の中心を保つ。charat.common.zoomG）
    /**
     * @param {number} scale
     * @param {number} orgW
     * @param {number} orgH
     * @returns {string}
     */
    function zoomTransform(scale, orgW, orgH) {
        return "translate(" + (orgW * scale - orgW) * -0.5 + ", " + (orgH * scale - orgH) * -0.5 + "),scale(" + scale + "," + scale + ")";
    }
    // 拡大率を1段変える（1±zoomMax の範囲、小数2桁に丸める。charat.common.zoomG）
    /**
     * @param {number} scale
     * @param {boolean} isZoom
     * @param {number} zoom
     * @param {number} zoomMax
     * @returns {number}
     */
    function nextScale(scale, isZoom, zoom, zoomMax) {
        var next = isZoom ? Math.min(1 + zoomMax, scale + zoom) : Math.max(1 - zoomMax, scale - zoom);
        return Math.round(next * 100) / 100;
    }
    // レイヤーの g の transform（moveG・reverseG・rotateG）。x・y は画面の座標（反転中は SIZE_CHARAT_IMAGE ずらした後）、
    // scx・scy は表示の倍率（反転中は scx が負）、center は回転の中心（rotateCenter）。center が null なら rotate を付けない（reverseG の回していないとき）
    /**
     * @param {number} x
     * @param {number} y
     * @param {number} scx
     * @param {number} scy
     * @param {number} rotate
     * @param {{ cx: number, cy: number } | null} center
     * @returns {string}
     */
    function layerTransform(x, y, scx, scy, rotate, center) {
        return "translate(" + x + ", " + y + "),scale(" + scx + "," + scy + ")" + (center ? ",rotate(" + rotate + "," + center.cx + "," + center.cy + ")" : "");
    }
    // 回転の中心: カテゴリの全レイヤー（中身のあるもの）を合わせた範囲のまん中（500 の画像の座標。charat.common.rotateCenter）
    /**
     * @param {ArrayLike<SVGGraphicsElement | null>} layers
     * @returns {{ cx: number, cy: number }}
     */
    function rotateCenter(layers) {
        var minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        each(layers, function (node) {
            if (!node) {
                return;
            }
            var bbox = null;
            try {
                bbox = node.getBBox(); // 表示されていない所では例外になるブラウザがある（その層は数えない。2026-10-20 に足した）
            } catch (e) {
                return;
            }
            if (!bbox.width && !bbox.height) {
                return;
            }
            minX = Math.min(minX, bbox.x);
            minY = Math.min(minY, bbox.y);
            maxX = Math.max(maxX, bbox.x + bbox.width);
            maxY = Math.max(maxY, bbox.y + bbox.height);
        });
        if (minX === Infinity) {
            return { cx: 0, cy: 0 };
        }
        return { cx: (minX + maxX) / 2, cy: (minY + maxY) / 2 };
    }

    // ---- 保存データの1行 ----
    // 標準の並び（行の [1] から。chibi.SAVE_FIELDS_STD はこれ。線の太さ paramSW は STROKE_WIDTH のメーカーだけが読み書きする）
    /** @type {string[]} */
    var SAVE_FIELDS_STD = [
        "paramParts", "paramCol", "paramColFrom", "paramColTo", "paramRev", "paramTx", "paramTy", "paramScale",
        "paramColLFrom", "paramColLTo", "paramColRFrom", "paramColRTo", "paramPtn", "paramPrev", "paramPtnMode",
        "paramRotate", "paramOpacity", "paramSW"
    ];
    // 並びが違うメーカー（saveFields。mono など）の行（split 済み）を標準の並びに直す（chibi.toStdSaveLine）
    /**
     * @param {string[]} aCols
     * @param {string[] | null} [saveFields]
     * @returns {string[]}
     */
    function toStdSaveLine(aCols, saveFields) {
        if (!saveFields || aCols.length <= 1) {
            return aCols;
        }
        var std = [aCols[0]];
        SAVE_FIELDS_STD.forEach(function (attrName, i) {
            std[i + 1] = "";
        });
        saveFields.forEach(function (attrName, i) {
            var stdIndex = SAVE_FIELDS_STD.indexOf(attrName);
            if (stdIndex >= 0 && aCols.length > i + 1) {
                std[stdIndex + 1] = aCols[i + 1];
            }
        });
        return std;
    }

    // ---- 選び方の決まり ----
    // ランダムのときのパーツ（chibi.makeCharactor）: RND が 1 以下ならどれか、2 以上なら RND - 2 番目。list はパーツ番号の順
    /**
     * @template T
     * @param {Category} category
     * @param {T[]} list
     * @returns {T}
     */
    function randomPart(category, list) {
        if (category.RND <= 1) {
            return list[Math.floor(Math.random() * list.length)]; // charat.common.getRandomArbitary(0, list.length - 1) と同じ
        }
        return list[category.RND - 2];
    }
    // 同じ GROUP のカテゴリ（色をまとめて付ける。自分も含む。chibi.getGroupCategory）
    /**
     * @param {Category[]} categories
     * @param {Category} sel
     * @returns {Category[]}
     */
    function groupCategories(categories, sel) {
        if (sel.GROUP != "") {
            return categories.filter(function (c) {
                return sel.GROUP == c.GROUP;
            });
        }
        return [sel];
    }
    // 同じ EXCLUDE のカテゴリ（どれか1つしか付けられない。自分も含む。chibi.getExcludeCategory）
    /**
     * @param {Category[]} categories
     * @param {Category} sel
     * @returns {Category[]}
     */
    function excludeCategories(categories, sel) {
        if (sel.EXCLUDE != "") {
            return categories.filter(function (c) {
                return sel.EXCLUDE == c.EXCLUDE;
            });
        }
        return [sel];
    }

    return {
        styleGet: styleGet,
        styleSet: styleSet,
        parseColor: parseColor,
        toRgb: toRgb,
        toHex: toHex,
        addRgb: addRgb,
        inverseColor: inverseColor,
        interpolateRgb: interpolateRgb,
        replaceGrad: replaceGrad,
        putPart: putPart,
        partLayers: partLayers,
        colorLayer: colorLayer,
        revColorLayer: revColorLayer,
        patternLayer: patternLayer,
        makePattern: makePattern,
        patternRev: patternRev,
        copyPattern: copyPattern,
        gradLayer: gradLayer,
        colorLine: colorLine,
        patternLine: patternLine,
        strokeWidthLayer: strokeWidthLayer,
        opacityLayer: opacityLayer,
        zoomTransform: zoomTransform,
        nextScale: nextScale,
        layerTransform: layerTransform,
        rotateCenter: rotateCenter,
        SAVE_FIELDS_STD: SAVE_FIELDS_STD,
        toStdSaveLine: toStdSaveLine,
        randomPart: randomPart,
        groupCategories: groupCategories,
        excludeCategories: excludeCategories
    };
})();
